import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Modal, Form, Row, Col, Alert, Badge } from 'react-bootstrap';
import { FaUserPlus, FaEdit, FaKey, FaBan, FaCheck } from 'react-icons/fa';
import { subadminAPI } from '../../utils/api';

const SubAdminManagement = () => {
  const [subAdmins, setSubAdmins] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedSubAdmin, setSelectedSubAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    position: '',
    fullAdminPrivileges: false,
    permissions: {
      dashboard: { view: true },
      personnel: { view: false, create: false, edit: false, disable: false },
      sites: { view: false, create: false, edit: false, delete: false, assign: false },
      statistics: { view: false, export: false },
      newClients: { view: false, export: false },
      coverage: { view: false, create: false, edit: false, delete: false, export: false },
      subAdmins: { view: false, create: false, edit: false, disable: false },
      structureManagement: { access: false },
      incidentReports: { access: false },
      specialAssignments: { access: false }
    }
  });
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    fetchSubAdmins();
  }, []);

  const fetchSubAdmins = async () => {
    try {
      const response = await subadminAPI.getAll();
      setSubAdmins(response.data);
      setError('');
    } catch (error) {
      console.error("Failed to fetch sub-admins:", error);
      setError("Failed to load sub-admin data. Please try again.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });
  };

  const handlePermissionChange = (module, action) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [module]: {
          ...formData.permissions[module],
          [action]: !formData.permissions[module][action]
        }
      }
    });
  };

  const handleFullAdminToggle = () => {
    const newValue = !formData.fullAdminPrivileges;
    
    // If enabling full admin privileges, set all permissions to true
    const updatedPermissions = newValue ? {
      dashboard: { view: true },
      personnel: { view: true, create: true, edit: true, disable: true },
      sites: { view: true, create: true, edit: true, delete: true, assign: true },
      statistics: { view: true, export: true },
      newClients: { view: true, export: true },
      coverage: { view: true, create: true, edit: true, delete: true, export: true },
      subAdmins: { view: true, create: true, edit: true, disable: true },
      structureManagement: { access: true },
      incidentReports: { access: true },
      specialAssignments: { access: true }
    } : formData.permissions;
    
    setFormData({
      ...formData,
      fullAdminPrivileges: newValue,
      permissions: updatedPermissions
    });
  };

  const handleAddSubAdmin = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }
    
    try {
      // Ensure all permissions are set if fullAdminPrivileges is enabled
      const dataToSend = {
        ...formData,
        permissions: formData.fullAdminPrivileges ? {
          dashboard: { view: true },
          personnel: { view: true, create: true, edit: true, disable: true },
          sites: { view: true, create: true, edit: true, delete: true, assign: true },
          statistics: { view: true, export: true },
          newClients: { view: true, export: true },
          coverage: { view: true, create: true, edit: true, delete: true, export: true },
          subAdmins: { view: true, create: true, edit: true, disable: true },
          structureManagement: { access: true },
          incidentReports: { access: true },
          specialAssignments: { access: true }
        } : formData.permissions
      };
      
      const response = await subadminAPI.create(dataToSend);
      setSubAdmins([...subAdmins, response.data]);
      setShowAddModal(false);
      resetForm();
      setSuccess('Admin added successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error("Failed to add sub-admin:", error);
      setError(error.response?.data?.message || "Failed to add sub-admin. Please try again.");
    }
  };

  const handleEditSubAdmin = (subAdmin) => {
    setSelectedSubAdmin(subAdmin);
    const defaultPermissions = {
      dashboard: { view: true },
      personnel: { view: false, create: false, edit: false, disable: false },
      sites: { view: false, create: false, edit: false, delete: false, assign: false },
      statistics: { view: false, export: false },
      newClients: { view: false, export: false },
      coverage: { view: false, create: false, edit: false, delete: false, export: false },
      subAdmins: { view: false, create: false, edit: false, disable: false },
      structureManagement: { access: false },
      incidentReports: { access: false },
      specialAssignments: { access: false }
    };
    
    // Merge existing permissions with defaults to handle old data without new permissions
    const mergedPermissions = {
      ...defaultPermissions,
      ...subAdmin.permissions,
      newClients: subAdmin.permissions?.newClients || defaultPermissions.newClients,
      coverage: subAdmin.permissions?.coverage || defaultPermissions.coverage,
      subAdmins: subAdmin.permissions?.subAdmins || defaultPermissions.subAdmins,
      structureManagement: subAdmin.permissions?.structureManagement || defaultPermissions.structureManagement,
      incidentReports: subAdmin.permissions?.incidentReports || defaultPermissions.incidentReports,
      specialAssignments: subAdmin.permissions?.specialAssignments || defaultPermissions.specialAssignments
    };
    
    // Check if sub-admin has full admin privileges
    // First check the fullAdminPrivileges flag from the backend
    let hasFullPrivileges = subAdmin.fullAdminPrivileges === true;
    
    // If not set, infer from permissions (for backward compatibility)
    if (!hasFullPrivileges) {
      hasFullPrivileges = 
        mergedPermissions.personnel?.view && mergedPermissions.personnel?.create && 
        mergedPermissions.personnel?.edit && mergedPermissions.personnel?.disable &&
        mergedPermissions.sites?.view && mergedPermissions.sites?.create && 
        mergedPermissions.sites?.edit && mergedPermissions.sites?.delete && 
        mergedPermissions.sites?.assign &&
        mergedPermissions.statistics?.view && mergedPermissions.statistics?.export &&
        mergedPermissions.newClients?.view && mergedPermissions.newClients?.export &&
        mergedPermissions.coverage?.view && mergedPermissions.coverage?.create && 
        mergedPermissions.coverage?.edit && mergedPermissions.coverage?.delete && 
        mergedPermissions.coverage?.export &&
        mergedPermissions.subAdmins?.view && mergedPermissions.subAdmins?.create && 
        mergedPermissions.subAdmins?.edit && mergedPermissions.subAdmins?.disable &&
        mergedPermissions.structureManagement?.access &&
        mergedPermissions.incidentReports?.access &&
        mergedPermissions.specialAssignments?.access;
    }
    
    setFormData({
      name: subAdmin.name,
      email: subAdmin.email,
      phone: subAdmin.phone || '',
      position: subAdmin.position || '',
      fullAdminPrivileges: hasFullPrivileges,
      permissions: mergedPermissions
    });
    setShowEditModal(true);
  };

  const handleUpdateSubAdmin = async (e) => {
    e.preventDefault();
    try {
      // Ensure all permissions are set if fullAdminPrivileges is enabled
      const dataToSend = {
        ...formData,
        permissions: formData.fullAdminPrivileges ? {
          dashboard: { view: true },
          personnel: { view: true, create: true, edit: true, disable: true },
          sites: { view: true, create: true, edit: true, delete: true, assign: true },
          statistics: { view: true, export: true },
          newClients: { view: true, export: true },
          coverage: { view: true, create: true, edit: true, delete: true, export: true },
          subAdmins: { view: true, create: true, edit: true, disable: true },
          structureManagement: { access: true },
          incidentReports: { access: true },
          specialAssignments: { access: true }
        } : formData.permissions
      };
      
      const response = await subadminAPI.update(selectedSubAdmin.id, dataToSend);
      setSubAdmins(subAdmins.map(admin => admin.id === selectedSubAdmin.id ? response.data : admin));
      setShowEditModal(false);
      resetForm();
      setSuccess('Admin updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error("Failed to update sub-admin:", error);
      setError(error.response?.data?.message || "Failed to update admin. Please try again.");
    }
  };

  const handlePasswordModal = (subAdmin) => {
    setSelectedSubAdmin(subAdmin);
    setPasswordData({
      password: '',
      confirmPassword: ''
    });
    setShowPasswordModal(true);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.password !== passwordData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (passwordData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }
    
    try {
      await subadminAPI.updatePassword(selectedSubAdmin.id, passwordData.password);
      setShowPasswordModal(false);
      setPasswordData({
        password: '',
        confirmPassword: ''
      });
      setSuccess('Password updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error("Failed to update password:", error);
      setError(error.response?.data?.message || "Failed to update password. Please try again.");
    }
  };

  const handleToggleStatus = async (subAdmin) => {
    try {
      const response = await subadminAPI.toggleStatus(subAdmin.id);
      setSubAdmins(subAdmins.map(admin => 
        admin.id === subAdmin.id 
          ? { ...admin, status: response.data.status } 
          : admin
      ));
      setSuccess(`Admin ${response.data.status === 'active' ? 'activated' : 'deactivated'} successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error("Failed to toggle sub-admin status:", error);
      setError(error.response?.data?.message || "Failed to update admin status. Please try again.");
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      position: '',
      fullAdminPrivileges: false,
      permissions: {
        dashboard: { view: true },
        personnel: { view: false, create: false, edit: false, disable: false },
        sites: { view: false, create: false, edit: false, delete: false, assign: false },
        statistics: { view: false, export: false },
        newClients: { view: false, export: false },
        coverage: { view: false, create: false, edit: false, delete: false, export: false },
        subAdmins: { view: false, create: false, edit: false, disable: false },
        structureManagement: { access: false },
        incidentReports: { access: false },
        specialAssignments: { access: false }
      }
    });
    setError('');
  };

  const filteredSubAdmins = subAdmins.filter(admin => 
    admin.name.toLowerCase().includes(filter.toLowerCase()) ||
    admin.email.toLowerCase().includes(filter.toLowerCase()) ||
    (admin.position && admin.position.toLowerCase().includes(filter.toLowerCase()))
  );

  return (
    <Container fluid>
      <h1 className="mb-4">Admin Management</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <Form.Control 
                type="text" 
                placeholder="Filter admins..." 
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-auto d-inline-block"
              />
            </div>
            <Button variant="primary" onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}>
              <FaUserPlus className="me-2" />
              Add New Admin
            </Button>
          </div>
          
          <Table responsive hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Position</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubAdmins.length > 0 ? (
                filteredSubAdmins.map(admin => (
                  <tr key={admin.id}>
                    <td>{admin.name}</td>
                    <td>{admin.email}</td>
                    <td>{admin.position || '-'}</td>
                    <td>
                      <Badge bg={admin.status === 'active' ? 'success' : 'danger'}>
                        {admin.status === 'active' ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleEditSubAdmin(admin)}
                      >
                        <FaEdit />
                      </Button>
                      <Button 
                        variant="outline-warning" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handlePasswordModal(admin)}
                      >
                        <FaKey />
                      </Button>
                      <Button 
                        variant={admin.status === 'active' ? 'outline-danger' : 'outline-success'} 
                        size="sm"
                        onClick={() => handleToggleStatus(admin)}
                      >
                        {admin.status === 'active' ? <FaBan /> : <FaCheck />}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    {filter ? 'No admins match your filter criteria.' : 'No admins found. Add an admin to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      
      {/* Add Sub-Admin Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Admin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddSubAdmin}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="name" 
                    value={formData.name} 
                    onChange={handleInputChange} 
                    required 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleInputChange} 
                    required 
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control 
                    type="password" 
                    name="password" 
                    value={formData.password} 
                    onChange={handleInputChange} 
                    required 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone (Optional)</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="phone" 
                    value={formData.phone} 
                    onChange={handleInputChange} 
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Position (Optional)</Form.Label>
              <Form.Control 
                type="text" 
                name="position" 
                value={formData.position} 
                onChange={handleInputChange} 
              />
            </Form.Group>
            
            {/* Full Admin Privileges Toggle */}
            <Alert variant="info" className="mt-4">
              <Form.Check 
                type="switch"
                id="full-admin-privileges"
                label={
                  <strong>
                    Grant Full Admin Privileges 
                    <small className="text-muted d-block">
                      Enable this to grant all permissions at once (same as main admin)
                    </small>
                  </strong>
                }
                checked={formData.fullAdminPrivileges}
                onChange={handleFullAdminToggle}
              />
            </Alert>
            
            <h5 className="mt-4">Permissions</h5>
            {formData.fullAdminPrivileges && (
              <Alert variant="success">
                <strong>Full Admin Mode Active:</strong> This sub-admin will have all permissions enabled.
              </Alert>
            )}
            <Card className="mb-3">
              <Card.Header>Dashboard</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="dashboard-view"
                  label="View Dashboard"
                  checked={formData.permissions.dashboard.view}
                  onChange={() => handlePermissionChange('dashboard', 'view')}
                  disabled // Dashboard view is always enabled for sub-admins
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Personnel Management</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="personnel-view"
                  label="View Personnel"
                  checked={formData.fullAdminPrivileges || formData.permissions.personnel.view}
                  onChange={() => handlePermissionChange('personnel', 'view')}
                  disabled={formData.fullAdminPrivileges}
                />
                <Form.Check 
                  type="switch"
                  id="personnel-create"
                  label="Create Personnel"
                  checked={formData.fullAdminPrivileges || formData.permissions.personnel.create}
                  onChange={() => handlePermissionChange('personnel', 'create')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.personnel.view}
                />
                <Form.Check 
                  type="switch"
                  id="personnel-edit"
                  label="Edit Personnel"
                  checked={formData.fullAdminPrivileges || formData.permissions.personnel.edit}
                  onChange={() => handlePermissionChange('personnel', 'edit')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.personnel.view}
                />
                <Form.Check 
                  type="switch"
                  id="personnel-disable"
                  label="Disable Personnel"
                  checked={formData.fullAdminPrivileges || formData.permissions.personnel.disable}
                  onChange={() => handlePermissionChange('personnel', 'disable')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.personnel.view}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Site Management</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="sites-view"
                  label="View Sites"
                  checked={formData.fullAdminPrivileges || formData.permissions.sites.view}
                  onChange={() => handlePermissionChange('sites', 'view')}
                  disabled={formData.fullAdminPrivileges}
                />
                <Form.Check 
                  type="switch"
                  id="sites-create"
                  label="Create Sites"
                  checked={formData.fullAdminPrivileges || formData.permissions.sites.create}
                  onChange={() => handlePermissionChange('sites', 'create')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.sites.view}
                />
                <Form.Check 
                  type="switch"
                  id="sites-edit"
                  label="Edit Sites"
                  checked={formData.fullAdminPrivileges || formData.permissions.sites.edit}
                  onChange={() => handlePermissionChange('sites', 'edit')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.sites.view}
                />
                <Form.Check 
                  type="switch"
                  id="sites-delete"
                  label="Delete Sites"
                  checked={formData.fullAdminPrivileges || formData.permissions.sites.delete}
                  onChange={() => handlePermissionChange('sites', 'delete')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.sites.view}
                />
                <Form.Check 
                  type="switch"
                  id="sites-assign"
                  label="Assign Personnel to Sites"
                  checked={formData.fullAdminPrivileges || formData.permissions.sites.assign}
                  onChange={() => handlePermissionChange('sites', 'assign')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.sites.view}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Statistics</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="statistics-view"
                  label="View Statistics"
                  checked={formData.fullAdminPrivileges || formData.permissions.statistics.view}
                  onChange={() => handlePermissionChange('statistics', 'view')}
                  disabled={formData.fullAdminPrivileges}
                />
                <Form.Check 
                  type="switch"
                  id="statistics-export"
                  label="Export Statistics"
                  checked={formData.fullAdminPrivileges || formData.permissions.statistics.export}
                  onChange={() => handlePermissionChange('statistics', 'export')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.statistics.view}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>New Clients</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="newClients-view"
                  label="View New Clients"
                  checked={formData.fullAdminPrivileges || formData.permissions.newClients.view}
                  onChange={() => handlePermissionChange('newClients', 'view')}
                  disabled={formData.fullAdminPrivileges}
                />
                <Form.Check 
                  type="switch"
                  id="newClients-export"
                  label="Export New Clients Report"
                  checked={formData.fullAdminPrivileges || formData.permissions.newClients.export}
                  onChange={() => handlePermissionChange('newClients', 'export')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.newClients.view}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Coverage</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="coverage-view"
                  label="View Coverage"
                  checked={formData.fullAdminPrivileges || formData.permissions.coverage.view}
                  onChange={() => handlePermissionChange('coverage', 'view')}
                  disabled={formData.fullAdminPrivileges}
                />
                <Form.Check 
                  type="switch"
                  id="coverage-create"
                  label="Create Coverage"
                  checked={formData.fullAdminPrivileges || formData.permissions.coverage.create}
                  onChange={() => handlePermissionChange('coverage', 'create')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.coverage.view}
                />
                <Form.Check 
                  type="switch"
                  id="coverage-edit"
                  label="Edit Coverage"
                  checked={formData.fullAdminPrivileges || formData.permissions.coverage.edit}
                  onChange={() => handlePermissionChange('coverage', 'edit')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.coverage.view}
                />
                <Form.Check 
                  type="switch"
                  id="coverage-delete"
                  label="Delete Coverage"
                  checked={formData.fullAdminPrivileges || formData.permissions.coverage.delete}
                  onChange={() => handlePermissionChange('coverage', 'delete')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.coverage.view}
                />
                <Form.Check 
                  type="switch"
                  id="coverage-export"
                  label="Export Coverage Data"
                  checked={formData.fullAdminPrivileges || formData.permissions.coverage.export}
                  onChange={() => handlePermissionChange('coverage', 'export')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.coverage.view}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Admin Management</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="subAdmins-view"
                  label="View Admins"
                  checked={formData.fullAdminPrivileges || formData.permissions.subAdmins.view}
                  onChange={() => handlePermissionChange('subAdmins', 'view')}
                  disabled={formData.fullAdminPrivileges}
                />
                <Form.Check 
                  type="switch"
                  id="subAdmins-create"
                  label="Create Admins"
                  checked={formData.fullAdminPrivileges || formData.permissions.subAdmins.create}
                  onChange={() => handlePermissionChange('subAdmins', 'create')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.subAdmins.view}
                />
                <Form.Check 
                  type="switch"
                  id="subAdmins-edit"
                  label="Edit Admins"
                  checked={formData.fullAdminPrivileges || formData.permissions.subAdmins.edit}
                  onChange={() => handlePermissionChange('subAdmins', 'edit')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.subAdmins.view}
                />
                <Form.Check 
                  type="switch"
                  id="subAdmins-disable"
                  label="Enable/Disable Admins"
                  checked={formData.fullAdminPrivileges || formData.permissions.subAdmins.disable}
                  onChange={() => handlePermissionChange('subAdmins', 'disable')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.subAdmins.view}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Structure Management</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="structureManagement-access"
                  label="Full Access to Structure Management"
                  checked={formData.fullAdminPrivileges || formData.permissions.structureManagement.access}
                  onChange={() => handlePermissionChange('structureManagement', 'access')}
                  disabled={formData.fullAdminPrivileges}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Incident Reports</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="incidentReports-access"
                  label="Full Access to Incident Reports"
                  checked={formData.fullAdminPrivileges || formData.permissions.incidentReports.access}
                  onChange={() => handlePermissionChange('incidentReports', 'access')}
                  disabled={formData.fullAdminPrivileges}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Special Assignments</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="specialAssignments-access"
                  label="Full Access to Special Assignments"
                  checked={formData.fullAdminPrivileges || formData.permissions.specialAssignments.access}
                  onChange={() => handlePermissionChange('specialAssignments', 'access')}
                  disabled={formData.fullAdminPrivileges}
                />
              </Card.Body>
            </Card>
            
            <div className="d-flex justify-content-end mt-4">
              <Button variant="secondary" className="me-2" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Add Admin
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Edit Sub-Admin Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Admin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdateSubAdmin}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="name" 
                    value={formData.name} 
                    onChange={handleInputChange} 
                    required 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleInputChange} 
                    required 
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone (Optional)</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="phone" 
                    value={formData.phone} 
                    onChange={handleInputChange} 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Position (Optional)</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="position" 
                    value={formData.position} 
                    onChange={handleInputChange} 
                  />
                </Form.Group>
              </Col>
            </Row>
            
            {/* Full Admin Privileges Toggle */}
            <Alert variant="info" className="mt-4">
              <Form.Check 
                type="switch"
                id="edit-full-admin-privileges"
                label={
                  <strong>
                    Grant Full Admin Privileges 
                    <small className="text-muted d-block">
                      Enable this to grant all permissions at once (same as main admin)
                    </small>
                  </strong>
                }
                checked={formData.fullAdminPrivileges}
                onChange={handleFullAdminToggle}
              />
            </Alert>
            
            <h5 className="mt-4">Permissions</h5>
            {formData.fullAdminPrivileges && (
              <Alert variant="success">
                <strong>Full Admin Mode Active:</strong> This sub-admin will have all permissions enabled.
              </Alert>
            )}
            <Card className="mb-3">
              <Card.Header>Dashboard</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="edit-dashboard-view"
                  label="View Dashboard"
                  checked={formData.permissions.dashboard.view}
                  onChange={() => handlePermissionChange('dashboard', 'view')}
                  disabled // Dashboard view is always enabled for sub-admins
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Personnel Management</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="edit-personnel-view"
                  label="View Personnel"
                  checked={formData.fullAdminPrivileges || formData.permissions.personnel.view}
                  onChange={() => handlePermissionChange('personnel', 'view')}
                  disabled={formData.fullAdminPrivileges}
                />
                <Form.Check 
                  type="switch"
                  id="edit-personnel-create"
                  label="Create Personnel"
                  checked={formData.fullAdminPrivileges || formData.permissions.personnel.create}
                  onChange={() => handlePermissionChange('personnel', 'create')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.personnel.view}
                />
                <Form.Check 
                  type="switch"
                  id="edit-personnel-edit"
                  label="Edit Personnel"
                  checked={formData.fullAdminPrivileges || formData.permissions.personnel.edit}
                  onChange={() => handlePermissionChange('personnel', 'edit')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.personnel.view}
                />
                <Form.Check 
                  type="switch"
                  id="edit-personnel-disable"
                  label="Disable Personnel"
                  checked={formData.fullAdminPrivileges || formData.permissions.personnel.disable}
                  onChange={() => handlePermissionChange('personnel', 'disable')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.personnel.view}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Site Management</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="edit-sites-view"
                  label="View Sites"
                  checked={formData.fullAdminPrivileges || formData.permissions.sites.view}
                  onChange={() => handlePermissionChange('sites', 'view')}
                  disabled={formData.fullAdminPrivileges}
                />
                <Form.Check 
                  type="switch"
                  id="edit-sites-create"
                  label="Create Sites"
                  checked={formData.fullAdminPrivileges || formData.permissions.sites.create}
                  onChange={() => handlePermissionChange('sites', 'create')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.sites.view}
                />
                <Form.Check 
                  type="switch"
                  id="edit-sites-edit"
                  label="Edit Sites"
                  checked={formData.fullAdminPrivileges || formData.permissions.sites.edit}
                  onChange={() => handlePermissionChange('sites', 'edit')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.sites.view}
                />
                <Form.Check 
                  type="switch"
                  id="edit-sites-delete"
                  label="Delete Sites"
                  checked={formData.fullAdminPrivileges || formData.permissions.sites.delete}
                  onChange={() => handlePermissionChange('sites', 'delete')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.sites.view}
                />
                <Form.Check 
                  type="switch"
                  id="edit-sites-assign"
                  label="Assign Personnel to Sites"
                  checked={formData.fullAdminPrivileges || formData.permissions.sites.assign}
                  onChange={() => handlePermissionChange('sites', 'assign')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.sites.view}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Statistics</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="edit-statistics-view"
                  label="View Statistics"
                  checked={formData.fullAdminPrivileges || formData.permissions.statistics.view}
                  onChange={() => handlePermissionChange('statistics', 'view')}
                  disabled={formData.fullAdminPrivileges}
                />
                <Form.Check 
                  type="switch"
                  id="edit-statistics-export"
                  label="Export Statistics"
                  checked={formData.fullAdminPrivileges || formData.permissions.statistics.export}
                  onChange={() => handlePermissionChange('statistics', 'export')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.statistics.view}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>New Clients</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="edit-newClients-view"
                  label="View New Clients"
                  checked={formData.fullAdminPrivileges || formData.permissions.newClients.view}
                  onChange={() => handlePermissionChange('newClients', 'view')}
                  disabled={formData.fullAdminPrivileges}
                />
                <Form.Check 
                  type="switch"
                  id="edit-newClients-export"
                  label="Export New Clients Report"
                  checked={formData.fullAdminPrivileges || formData.permissions.newClients.export}
                  onChange={() => handlePermissionChange('newClients', 'export')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.newClients.view}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Coverage</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="edit-coverage-view"
                  label="View Coverage"
                  checked={formData.fullAdminPrivileges || formData.permissions.coverage.view}
                  onChange={() => handlePermissionChange('coverage', 'view')}
                  disabled={formData.fullAdminPrivileges}
                />
                <Form.Check 
                  type="switch"
                  id="edit-coverage-create"
                  label="Create Coverage"
                  checked={formData.fullAdminPrivileges || formData.permissions.coverage.create}
                  onChange={() => handlePermissionChange('coverage', 'create')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.coverage.view}
                />
                <Form.Check 
                  type="switch"
                  id="edit-coverage-edit"
                  label="Edit Coverage"
                  checked={formData.fullAdminPrivileges || formData.permissions.coverage.edit}
                  onChange={() => handlePermissionChange('coverage', 'edit')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.coverage.view}
                />
                <Form.Check 
                  type="switch"
                  id="edit-coverage-delete"
                  label="Delete Coverage"
                  checked={formData.fullAdminPrivileges || formData.permissions.coverage.delete}
                  onChange={() => handlePermissionChange('coverage', 'delete')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.coverage.view}
                />
                <Form.Check 
                  type="switch"
                  id="edit-coverage-export"
                  label="Export Coverage Data"
                  checked={formData.fullAdminPrivileges || formData.permissions.coverage.export}
                  onChange={() => handlePermissionChange('coverage', 'export')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.coverage.view}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Admin Management</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="edit-subAdmins-view"
                  label="View Admins"
                  checked={formData.fullAdminPrivileges || formData.permissions.subAdmins.view}
                  onChange={() => handlePermissionChange('subAdmins', 'view')}
                  disabled={formData.fullAdminPrivileges}
                />
                <Form.Check 
                  type="switch"
                  id="edit-subAdmins-create"
                  label="Create Admins"
                  checked={formData.fullAdminPrivileges || formData.permissions.subAdmins.create}
                  onChange={() => handlePermissionChange('subAdmins', 'create')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.subAdmins.view}
                />
                <Form.Check 
                  type="switch"
                  id="edit-subAdmins-edit"
                  label="Edit Admins"
                  checked={formData.fullAdminPrivileges || formData.permissions.subAdmins.edit}
                  onChange={() => handlePermissionChange('subAdmins', 'edit')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.subAdmins.view}
                />
                <Form.Check 
                  type="switch"
                  id="edit-subAdmins-disable"
                  label="Enable/Disable Admins"
                  checked={formData.fullAdminPrivileges || formData.permissions.subAdmins.disable}
                  onChange={() => handlePermissionChange('subAdmins', 'disable')}
                  disabled={formData.fullAdminPrivileges || !formData.permissions.subAdmins.view}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Structure Management</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="edit-structureManagement-access"
                  label="Full Access to Structure Management"
                  checked={formData.fullAdminPrivileges || formData.permissions.structureManagement.access}
                  onChange={() => handlePermissionChange('structureManagement', 'access')}
                  disabled={formData.fullAdminPrivileges}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Incident Reports</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="edit-incidentReports-access"
                  label="Full Access to Incident Reports"
                  checked={formData.fullAdminPrivileges || formData.permissions.incidentReports.access}
                  onChange={() => handlePermissionChange('incidentReports', 'access')}
                  disabled={formData.fullAdminPrivileges}
                />
              </Card.Body>
            </Card>
            
            <Card className="mb-3">
              <Card.Header>Special Assignments</Card.Header>
              <Card.Body>
                <Form.Check 
                  type="switch"
                  id="edit-specialAssignments-access"
                  label="Full Access to Special Assignments"
                  checked={formData.fullAdminPrivileges || formData.permissions.specialAssignments.access}
                  onChange={() => handlePermissionChange('specialAssignments', 'access')}
                  disabled={formData.fullAdminPrivileges}
                />
              </Card.Body>
            </Card>
            
            <div className="d-flex justify-content-end mt-4">
              <Button variant="secondary" className="me-2" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Update Admin
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Change Password Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdatePassword}>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control 
                type="password" 
                name="password" 
                value={passwordData.password} 
                onChange={handlePasswordChange} 
                required 
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control 
                type="password" 
                name="confirmPassword" 
                value={passwordData.confirmPassword} 
                onChange={handlePasswordChange} 
                required 
              />
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowPasswordModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Update Password
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default SubAdminManagement;
